export interface Notification {
  id: string
  userId: string
  profileId: string
  /** Firestore document path (e.g. profiles/{id}/notifications/{id}); set when loaded from Firestore */
  documentPath?: string
  type: NotificationType
  title: string
  message: string
  data?: Record<string, any>
  status: NotificationStatus
  priority: NotificationPriority
  createdAt: Date
  readAt?: Date
  expiresAt?: Date
  actionUrl?: string
  actionText?: string
  isAccountLevel?: boolean
}

export enum NotificationType {
  // Profile sharing
  PROFILE_SHARED = 'profile_shared',
  PROFILE_SHARE_ACCEPTED = 'profile_share_accepted',
  PROFILE_SHARE_DECLINED = 'profile_share_declined',
  PROFILE_SHARE_EXPIRED = 'profile_share_expired',
  
  // Genetic processing
  GENETIC_UPLOAD_COMPLETED = 'genetic_upload_completed',
  GENETIC_ANALYSIS_COMPLETED = 'genetic_analysis_completed',
  GENETIC_REPORT_READY = 'genetic_report_ready',
  GENETIC_PROCESSING_ERROR = 'genetic_processing_error',
  
  // Health tracking
  WELLNESS_GOAL_REMINDER = 'wellness_goal_reminder',
  HEALTH_CHECKIN_DUE = 'health_checkin_due',
  MEDICATION_REMINDER = 'medication_reminder',
  APPOINTMENT_REMINDER = 'appointment_reminder',
  GOAL_PROGRESS_SHARED = 'goal_progress_shared',
  
  // System
  SYSTEM_UPDATE = 'system_update',
  MAINTENANCE_NOTICE = 'maintenance_notice',
  SECURITY_ALERT = 'security_alert',
  
  // Collaboration
  COLLABORATOR_JOINED = 'collaborator_joined',
  COLLABORATOR_LEFT = 'collaborator_left',
  ACCESS_REQUEST = 'access_request',
  ACCESS_REVOKED = 'access_revoked',
  
  // Billing
  SUBSCRIPTION_EXPIRING = 'subscription_expiring',
  PAYMENT_FAILED = 'payment_failed',
  USAGE_LIMIT_WARNING = 'usage_limit_warning',
  
  // DNA Kits
  DNA_KIT_PURCHASED = 'dna_kit_purchased',
  DNA_KIT_SHIPPED = 'dna_kit_shipped',
  DNA_KIT_RECEIVED = 'dna_kit_received',
  DNA_KIT_REGISTRATION_REMINDER = 'dna_kit_registration_reminder',
  DNA_KIT_RESULTS_READY = 'dna_kit_results_ready',
  
  // Chat and AI
  GENECHAT_RESPONSE = 'genechat_response',
  NEW_MESSAGE = 'new_message',
  SUPPORT_UPDATE = 'support_update'
}

export enum NotificationStatus {
  PENDING = 'pending',
  SENT = 'sent',
  DELIVERED = 'delivered',
  READ = 'read',
  FAILED = 'failed',
  EXPIRED = 'expired'
}

export enum NotificationPriority {
  LOW = 'low',
  NORMAL = 'normal',
  HIGH = 'high',
  URGENT = 'urgent'
}

export interface NotificationPreferences {
  email: boolean
  push: boolean
  sms: boolean
  inApp: boolean
  
  // Type-specific preferences
  types: {
    [key in NotificationType]: {
      email: boolean
      push: boolean
      sms: boolean
      inApp: boolean
    }
  }
  
  // Quiet hours
  quietHoursEnabled: boolean
  quietHoursStart?: string // HH:MM format
  quietHoursEnd?: string // HH:MM format
  quietHoursTimezone?: string
}

export interface NotificationCounts {
  total: number
  unread: number
  byType: Record<NotificationType, number>
  byPriority: Record<NotificationPriority, number>
}

export interface NotificationFilters {
  types?: NotificationType[]
  status?: NotificationStatus[]
  priority?: NotificationPriority[]
  profileId?: string
  dateRange?: {
    start: Date
    end: Date
  }
  read?: boolean
}

export interface NotificationAction {
  type: 'navigate' | 'open_modal' | 'external_link' | 'dismiss' | 'custom'
  url?: string
  modalId?: string
  action?: string
  data?: Record<string, any>
}
